import { Game, GameFormula } from "../models/game.model";

/**
 * Replaces placeholders in a format string with the provided arguments.
 *
 * The function searches for `%s` placeholders in the `format` string and replaces
 * them sequentially with the values provided in the `args` array.
 *
 * @param format - The string containing `%s` placeholders to be replaced.
 * @param args - The values to replace the `%s` placeholders with.
 * @returns The formatted string with placeholders replaced by the corresponding arguments.
 *
 * @example
 * ```typescript
 * const result = sprintf("Hello, %s! You have %s new messages.", "Alice", 5);
 * console.log(result); // Output: "Hello, Alice! You have 5 new messages."
 * ```
 */
export function sprintf(format: string, ...args: any[]) {
    let i = 0;
    return format.replace(/%s/g, () => args[i++]);
  }

/**
 * Formats a selection string based on the provided base and associate selections,
 * game base number, partant pool, and game formula.
 *
 * @param base - An array of strings representing the base selections.
 * @param associates - An array of strings representing the associate selections.
 * @param nGameBaseNo - The number of base selections required for the game. Defaults to 0.
 * @param nPartantPool - The total number of selections in the partant pool.
 * @param gameFormula - The formula of the game, which determines specific formatting rules.
 * @returns A formatted string representing the selection, with base and associate selections
 *          separated by " / " and padded with "X" where necessary.
 */
export function formatSelectionString(
  base: string[],
  associates: string[],
  nGameBaseNo: number = 0,
  nPartantPool: number = 0,
  gameFormula: GameFormula,
  isSpotEnabled: boolean,
  spotJeu?: Game['spot'],
): string {
  let result = "";

  // handles CT   
  if (gameFormula === "CT") {
    // If base.length >= nGameBaseNo - 1, pre-fill the last value with "X"
    if (base.length >= nGameBaseNo - 1) {
      base = base.concat(Array(Math.max(0, nGameBaseNo - base.length)).fill("X"));
    } else if (base.length > 0) {
      base = base.concat(Array(Math.max(0, nPartantPool - nGameBaseNo)).fill("X"));
    }
    return base.slice(0, nGameBaseNo).join(" - "); 
  }
  
  // handles the rest of the game formulas
  const baseCutOff = ( isSpotEnabled ? base.length : ["CE"].includes(gameFormula) ? nPartantPool : nGameBaseNo);  
  const completeBase = ["CP", "CT"].includes(gameFormula);
  
  const formattedBase = base
    .concat(isSpotEnabled ? (base.length == (nGameBaseNo - 1) ? Array(Math.max(0, (spotJeu?.maxHorseCount ?? 0) - 1)).fill("X") : []): completeBase ? Array(Math.max(0, nGameBaseNo - base.length)).fill("X") : [])
    .slice(0, baseCutOff)
    .join(" - ");
   result = formattedBase;

  if (completeBase && associates.length > 0) {
    const formattedAssociates = associates
      .slice(0, Math.max(0, nPartantPool - nGameBaseNo))
      .join(" - ");
    result += ` / ${formattedAssociates}`;
  }

  return result;
}

export function formatPhoneNumber(phoneCode: string, phoneNumber: string): string {
  // Ensure the phone code starts with a '+'
  const formattedCode = phoneCode.startsWith('+') ? phoneCode : `+${phoneCode}`;
  // Return the formatted phone number
  return `(${formattedCode}) ${phoneNumber}`;
}

export function formatTicketData(data: string[]) {
    let fmt: any[] = data.join(',').split('-'.repeat(31));
    let display = fmt.splice(3);
    return data;
}